/** Sidebar route config and access helpers (shared by SideBar, Login, route guard). */

/** Read-only product preview routes (eye icon from inventory / marketplace lists). */
export const MARKETPLACE_PREVIEW_FRAGMENTS = [
  "marketplace/viewproduct",
  "marketplace/mediaviewproduct",
  "marketplace/multiplexmediaviewproduct",
  "marketplace/hoardingmediaviewproduct",
  "marketplace/valueandgiftvoucher",
  "marketplace/spacificvoucher",
];

export function isMarketplacePreviewPath(pathname) {
  const p = String(pathname || "").toLowerCase();
  return MARKETPLACE_PREVIEW_FRAGMENTS.some((frag) => p.includes(frag));
}

/** Map legacy DB permission keys to current sidebar access keys. */
export const normalizeAccessForUi = (acess, adminType = "brand_world") => {
  if (!Array.isArray(acess)) return [];
  const legacyMap = {
    ADMINHANDLES: "HANDLES",
    ADMINTRAILS: "HANDLES",
    HANDLES_BW: "HANDLES",
    BRAND_WORLD_WALLET: "BXI_WALLET",
    BW_WALLET: "BXI_WALLET",
    EINVOICE: "BW_TAX_INVOICES",
  };

  const normalized = [];
  for (const raw of acess) {
    const key = String(raw || "").trim();
    if (!key) continue;
    if (key === "All") return ["All"];
    if (legacyMap[key]) {
      normalized.push(legacyMap[key]);
      continue;
    }
    if (adminType === "brand_world" && key === "TRANSACTIONS") {
      normalized.push("RELEASEFUNDS");
      continue;
    }
    normalized.push(key);
  }
  return [...new Set(normalized)];
};

/** Allow preview when user can open inventory / marketplace, not only full MARKETPLACE menu. */
export function hasMarketplacePreviewAccess(adminType = "brand_world", acess = []) {
  const access = normalizeAccessForUi(acess, adminType);
  if (access.includes("All")) return true;
  if (adminType === "brand_world") {
    return access.includes("MARKETPLACE");
  }
  return access.includes("PRODUCTS") || access.includes("STOCK_INVENTORY");
}

export function getDerivedActiveTokens(pathnameLower, searchLower = "") {
  const tokens = [];
  const parts = pathnameLower.split("/").filter(Boolean);

  // companylanding is shared between the Leads and Members listings. When the
  // user opened the company from Leads (tagged via ?from=leads), keep the Leads
  // menu active instead of jumping to Members -> Live Members.
  const fromLeads = searchLower.includes("from=leads");
  if (pathnameLower.includes("companylanding")) {
    if (fromLeads) tokens.push("leads");
    else tokens.push("members", "live-members");
  }
  if (pathnameLower.includes("memberdirectory")) tokens.push("members", "live-members");
  if (pathnameLower.includes("companyinvoicedetail")) tokens.push("members", "live-members");

  const evIdx = parts.findIndex((s) => s === "einvoiceview");
  if (evIdx !== -1 && parts[evIdx + 2]) {
    const viewType = parts[evIdx + 2];
    if (viewType === "mem") {
      tokens.push("membership-renewals", "membershiprenewal");
    }
    if (viewType === "com") {
      tokens.push("commissioninvoicelist", "e-invoices");
    }
  }

  // Brand World tax invoices (bwtaxinvoicelist / bwtaxinvoice) belong to the
  // E-Invoice menu, not Transactions. Guard the generic "taxinvoice" substring
  // so it doesn't also light up Transactions for these paths.
  const isBwTaxInvoicePath = pathnameLower.includes("bwtaxinvoice");
  if (
    pathnameLower.includes("orderrequest") ||
    pathnameLower.includes("performainvoice") ||
    pathnameLower.includes("mediaproformainvoice") ||
    (pathnameLower.includes("taxinvoice") && !isBwTaxInvoicePath) ||
    pathnameLower.includes("choose-transport") ||
    pathnameLower.includes("commissionbyorderid")
  ) {
    tokens.push("bw-sales", "orders");
  }

  const onMarketplacePreview = MARKETPLACE_PREVIEW_FRAGMENTS.some((frag) =>
    pathnameLower.includes(frag)
  );
  if (pathnameLower.includes("bxi_marketplace") || onMarketplacePreview) {
    tokens.push("products/live");
  }
  if (onMarketplacePreview) {
    tokens.push("bw-marketplace");
  }

  if (pathnameLower.includes("allproductapprovals")) {
    tokens.push("products/pending");
  }

  if (pathnameLower.includes("memamountdebitstatus")) {
    tokens.push("membership-renewals", "membershiprenewal");
  }

  if (pathnameLower.includes("bxicommissionlist")) {
    tokens.push("commissioninvoicelist", "e-invoices");
  }

  if (
    pathnameLower.includes("bwtdsdebitnotelist") ||
    pathnameLower.includes("bwtdsdebitnote")
  ) {
    tokens.push("bwtdsdebitnotelist", "e-invoices");
  }

  if (
    pathnameLower.includes("bwtaxinvoicelist") ||
    pathnameLower.includes("bwtaxinvoice")
  ) {
    tokens.push("bwtaxinvoicelist");
  }

  return tokens;
}

const accessMatchesRoute = (route, accessItem) => {
  if (!route?.match) return false;
  if (route.match === accessItem) return true;
  if (Array.isArray(route.matchAliases) && route.matchAliases.includes(accessItem)) {
    return true;
  }
  return false;
};

export const ADMIN_SIDEBAR_ROUTES = [
  {
    id: 1,
    name: "Admin Dashboard",
    match: "ADMINDASHBOARD",
    linkUrl: "userdashboard",
    // "needs-attention" keeps this menu item highlighted while the (sidebar-hidden)
    // Needs Attention page + its bucket lists are open — they are entered from the
    // Admin Dashboard, so it stays the active parent.
    activeMenuFor: [
      "userdashboard",
      "banner",
      "analytics",
      "seller-payouts",
      "needs-attention",
      "ledger-reconciliation",
    ],
    adminType: "both",
  },
  {
    // Operations queue — surfaces everything actionable (stuck orders, stalled
    // onboarding, unpaid TDS, expiring credits). Entered from a button on the
    // Admin Dashboard page, NOT the sidebar menu — so it's marked hidden but
    // kept here so the route stays accessible (isAdminPathAllowed derives page
    // access from this list). Shares Admin Dashboard access.
    id: 110,
    name: "Needs Attention",
    match: "ADMINDASHBOARD",
    matchAliases: ["ANALYTICS"],
    linkUrl: "needs-attention",
    activeMenuFor: ["needs-attention"],
    adminType: "brand_world",
    hidden: true,
  },
  {
    // Notification monitor — ops view over the v2 notification engine: what was
    // sent, what failed and why, what is queued, plus a test-send console.
    // Entered from Admin Dashboard > Utility, like Needs Attention and Ledger
    // Reconciliation, so it is hidden from the sidebar but kept here:
    // isAdminPathAllowed derives page access from this list. Access rides on
    // ANALYTICS, the same grant the other ops screens use.
    id: 112,
    name: "Notification Monitor",
    match: "ADMINDASHBOARD",
    matchAliases: ["ANALYTICS"],
    linkUrl: "notification-monitor",
    activeMenuFor: ["notification-monitor"],
    adminType: "brand_world",
    hidden: true,
  },
  {
    // Ledger reconciliation monitor — also entered from an Admin Dashboard button,
    // hidden from the sidebar but kept so the route stays accessible.
    id: 111,
    name: "Ledger Reconciliation",
    match: "ADMINDASHBOARD",
    matchAliases: ["ANALYTICS"],
    linkUrl: "ledger-reconciliation",
    activeMenuFor: ["ledger-reconciliation"],
    adminType: "brand_world",
    hidden: true,
  },
  {
    id: 101,
    name: "Leads",
    match: "LEADS",
    matchAliases: ["USERANDCOMPANY"],
    linkUrl: "leads",
    activeMenuFor: ["leads", "companies"],
    adminType: "bxi",
  },
  {
    id: 102,
    name: "Members",
    match: "MEMBERS",
    matchAliases: ["USERANDCOMPANY", "MEMBERSHIPLIST", "MEMBERSHIPRENEWAL"],
    linkUrl: "members/live-members",
    // "companylanding" is intentionally NOT listed here: it is a shared route
    // resolved via getDerivedActiveTokens so Leads-origin views don't light up
    // Members. The derived "members" token covers the Members-origin case.
    activeMenuFor: ["members"],
    adminType: "bxi",
    children: [
      {
        id: "102a",
        name: "Live Members",
        linkUrl: "members/live-members",
        activeMenuFor: ["live-members"],
      },
      {
        id: "102b",
        name: "Membership Renewals",
        linkUrl: "members/membership-renewals",
        activeMenuFor: ["membership-renewals", "membershiprenewal"],
      },
    ],
  },
  {
    id: 103,
    name: "Transactions",
    match: "TRANSACTIONS",
    matchAliases: ["RELEASEFUNDS"],
    linkUrl: "transactions/bw-sales",
    activeMenuFor: ["transactions", "orders", "orderrequest"],
    adminType: "bxi",
    children: [
      {
        id: "103a",
        name: "BW Sales",
        linkUrl: "transactions/bw-sales",
        activeMenuFor: ["bw-sales"],
      },
      {
        id: "103b",
        name: "BW Purchase",
        linkUrl: "transactions/bw-purchase",
        activeMenuFor: ["bw-purchase"],
      },
    ],
  },
  {
    id: 104,
    name: "Products",
    match: "PRODUCTS",
    matchAliases: ["BXI_MARKETPLACE"],
    linkUrl: "products/live",
    activeMenuFor: [
      "products/",
      "bxi_marketplace",
      "approvallisting",
      "allproductapprovals",
    ],
    adminType: "bxi",
    children: [
      {
        id: "104a",
        name: "Live",
        linkUrl: "products/live",
        activeMenuFor: ["products/live"],
      },
      {
        id: "104b",
        name: "Pending",
        linkUrl: "products/pending",
        activeMenuFor: ["products/pending"],
      },
      {
        id: "104c",
        name: "Delisted / Rejected",
        linkUrl: "products/delisted",
        activeMenuFor: ["products/delisted"],
      },
    ],
  },
  {
    id: 105,
    name: "Stock Inventory",
    match: "STOCK_INVENTORY",
    linkUrl: "stock-inventory",
    activeMenuFor: [
      "stock-inventory",
      "inward-movement",
      "outward-movement",
      "net-movement",
      "total-products",
      "product_detail",
      "CompanyDetails",
      "bw-marketplace",
      ...MARKETPLACE_PREVIEW_FRAGMENTS,
    ],
    adminType: "bxi",
  },
  {
    id: 106,
    name: "Handles",
    match: "HANDLES",
    matchAliases: ["ADMINHANDLES", "ADMINTRAILS", "HANDLES_BW"],
    linkUrl: "handles",
    activeMenuFor: ["handles", "adminhandles", "admintrails", "memberdetails"],
    adminType: "bxi",
  },
  {
    id: 107,
    name: "E-Invoices",
    match: "EINVOICES",
    matchAliases: ["MEMBERSHIPLIST", "MEMBERSHIPRENEWAL", "EINVOICE"],
    linkUrl: "e-invoices",
    activeMenuFor: [
      "e-invoices",
      "membershipinvoicelist",
      "commissioninvoicelist",
      "bxicommissionlist",
      "bwtdsdebitnotelist",
    ],
    adminType: "bxi",
  },
  {
    id: 17,
    name: "Brand World Wallet",
    match: "BXI_WALLET",
    matchAliases: ["BRAND_WORLD_WALLET", "BW_WALLET"],
    linkUrl: "bwwallet",
    activeMenuFor: ["bwwallet"],
    adminType: "brand_world",
  },
  {
    // Hidden from the sidebar menu, but kept here so the route stays accessible
    // (page access is derived from this list via isAdminPathAllowed).
    id: 171,
    name: "Seller Payouts",
    match: "BXI_WALLET",
    matchAliases: ["BRAND_WORLD_WALLET", "BW_WALLET"],
    linkUrl: "seller-payouts",
    activeMenuFor: ["seller-payouts"],
    adminType: "brand_world",
    hidden: true,
  },
  {
    id: 2,
    name: "Marketplace",
    match: "MARKETPLACE",
    linkUrl: "bw-marketplace",
    activeMenuFor: ["bw-marketplace", ...MARKETPLACE_PREVIEW_FRAGMENTS],
    adminType: "brand_world",
  },
  {
    id: 6,
    name: "Transactions",
    match: "RELEASEFUNDS",
    matchAliases: ["TRANSACTIONS"],
    linkUrl: "orders",
    activeMenuFor: ["orders", "orderrequest"],
    adminType: "brand_world",
  },
  {
    // Offline Payments / Beneficiaries / BW Bank & QR — nested like Products.
    id: 123,
    name: "Bank and Beneficiaries",
    match: "RELEASEFUNDS",
    matchAliases: ["TRANSACTIONS", "ORDERREQUESTS", "HANDLES"],
    linkUrl: "offline-payments",
    activeMenuFor: [
      "offline-payments",
      "bw-beneficiaries",
      "bw-payment-settings",
      "bank-and-bene",
    ],
    adminType: "brand_world",
    children: [
      {
        id: "123a",
        name: "Offline Payments",
        linkUrl: "offline-payments",
        activeMenuFor: ["offline-payments"],
      },
      {
        id: "123b",
        name: "Beneficiaries",
        linkUrl: "bw-beneficiaries",
        activeMenuFor: ["bw-beneficiaries"],
      },
      {
        id: "123c",
        name: "BW Bank & QR",
        linkUrl: "bw-payment-settings",
        activeMenuFor: ["bw-payment-settings"],
      },
    ],
  },
  {
    id: 8,
    name: "All Records",
    match: "ALLRECORDS",
    linkUrl: "allrecords",
    activeMenuFor: [
      "allrecords",
      "invoices",
      "admindashboard/approvals",
      "interests",
      "delivery",
      "wishlist",
    ],
    adminType: "brand_world",
  },
  {
    id: 18,
    name: "E-Invoice",
    match: "BW_TAX_INVOICES",
    matchAliases: ["EINVOICE"],
    linkUrl: "bwtaxinvoicelist",
    activeMenuFor: ["bwtaxinvoicelist", "bwtaxinvoice"],
    adminType: "brand_world",
  },
  {
    id: 19,
    name: "Handles",
    match: "HANDLES",
    matchAliases: ["ADMINHANDLES", "ADMINTRAILS", "HANDLES_BW"],
    linkUrl: "handles",
    activeMenuFor: ["handles", "adminhandles", "admintrails", "memberdetails"],
    adminType: "brand_world",
  },
  {
    id: 7,
    name: "Help and Feedback",
    match: "HELPANDFEEDBACK",
    linkUrl: "helpandfeedback",
    activeMenuFor: ["helpandfeedback"],
    adminType: "brand_world",
  },
  {
    id: 16,
    name: "TDS/GST",
    match: "TDS_GST",
    linkUrl: "tdsgst",
    activeMenuFor: ["tdsgst"],
    adminType: "brand_world",
  },
  {
    id: 9,
    name: "External API Reports",
    match: "EXTERNALAPIREPORTS",
    linkUrl: "allexternalapi",
    activeMenuFor: ["allexternalapi", "signzydata", "pinelabsdata", "escrowdata", "pickrrdata", "vendorapiusage"],
    adminType: "bxi",
  },
  {
    id: 15,
    name: "Recycle Bin",
    match: "RECYLEBIN",
    linkUrl: "trash",
    activeMenuFor: ["producttrash", "trash"],
    adminType: "bxi",
  },
  {
    id: 109,
    name: "Notifications",
    match: "NOTIFICATIONS",
    linkUrl: "notifications",
    activeMenuFor: ["notifications"],
    adminType: "brand_world",
  },
];

export const getFilteredAdminRoutes = (adminType = "brand_world", acess = []) => {
  const routesByAdminType = ADMIN_SIDEBAR_ROUTES.filter(
    (route) => route.adminType === adminType || route.adminType === "both"
  );

  const accessList = normalizeAccessForUi(acess, adminType);
  if (accessList.includes("All")) {
    return routesByAdminType;
  }
  if (accessList.length === 0) {
    return [];
  }

  const filteredRoutes = [];
  accessList.forEach((item) => {
    routesByAdminType.forEach((route) => {
      if (accessMatchesRoute(route, item)) {
        const already = filteredRoutes.some((r) => r.id === route.id);
        if (!already) filteredRoutes.push(route);
      }
    });
  });
  return filteredRoutes;
};

export const getDefaultAdminPath = (adminType = "brand_world", acess = []) => {
  const routes = getFilteredAdminRoutes(adminType, acess).filter(
    (route) => route.linkUrl && !route.hidden
  );
  // Both BXI and Brand World admin logins should land on Admin Dashboard first.
  const adminDashboard = routes.find(
    (route) => route.linkUrl === "userdashboard" || route.match === "ADMINDASHBOARD"
  );
  if (adminDashboard?.linkUrl) return `/admindashboard/${adminDashboard.linkUrl}`;
  const portalSpecific = routes.find((route) => route.adminType === adminType);
  const first = portalSpecific || routes[0];
  if (first?.linkUrl) return `/admindashboard/${first.linkUrl}`;
  return "/admindashboard/userdashboard";
};

const collectMenuTokens = (routes) => {
  const tokens = new Set();
  routes.forEach((route) => {
    (route.activeMenuFor || []).forEach((t) => tokens.add(String(t).toLowerCase()));
    if (route.linkUrl) tokens.add(String(route.linkUrl).toLowerCase());
    (route.children || []).forEach((child) => {
      (child.activeMenuFor || []).forEach((t) => tokens.add(String(t).toLowerCase()));
      if (child.linkUrl) tokens.add(String(child.linkUrl).toLowerCase());
    });
  });
  return tokens;
};

export const isAdminPathAllowed = (pathname, adminType = "brand_world", acess = []) => {
  const pathnameLower = String(pathname || "").toLowerCase();
  if (
    pathnameLower === "/admindashboard" ||
    pathnameLower === "/admindashboard/"
  ) {
    return false;
  }

  if (isMarketplacePreviewPath(pathnameLower) && hasMarketplacePreviewAccess(adminType, acess)) {
    return true;
  }

  const allowedRoutes = getFilteredAdminRoutes(adminType, acess);
  if (!allowedRoutes.length) {
    return adminType === "brand_world" && pathnameLower.includes("/notifications");
  }
  const menuTokens = collectMenuTokens(allowedRoutes);
  const derived = getDerivedActiveTokens(pathnameLower);

  const pathMatchesToken = (token) => {
    const t = String(token).toLowerCase();
    if (!t) return false;
    return pathnameLower.includes(t) || derived.includes(t);
  };

  return [...menuTokens].some(pathMatchesToken);
};
